import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest, profileRequest } from "../authConfig";
//import { getInfoUsers } from "../services/ApiGetUserInfo";
import { callMsGraphGroup } from "../graphGroup";
import LoadingSpinner from "../shared/LoadingSpinner";
import axios from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { AccessError, accessErrorFromMsal, readLoginError } from "../helpers/accessError";
import { AccessErrorPage } from "../pages/AccessErrorPage";
import { getPersonId } from "../services/ApiGetPersonId";

// Per-user data kept in sessionStorage; cleared when another account signs in on the same tab.
const userScopedKeys = ["obj_user_info", "usergroup", "AccessToken", "GraphAccessToken", "salary_request_detail_cache", "salary_request_detail_timestamp"];
const userScopedPrefixes = ["cache_user_info_", "person_id_", "attendance_"];

// One interactive redirect is allowed per this window; another failure in it shows an error page instead of looping.
const interactionRedirectKey = "interaction_redirect_at";
const interactionRedirectWindowMs = 2 * 60 * 1000;

const clearUserScopedStorage = () => {
  userScopedKeys.forEach((key) => sessionStorage.removeItem(key));
  Object.keys(sessionStorage)
    .filter((key) => userScopedPrefixes.some((prefix) => key.startsWith(prefix)))
    .forEach((key) => sessionStorage.removeItem(key));
  localStorage.removeItem("obj_user_info");
};

// The stored token is reused only until the expiry saved with it; after that MSAL is asked again, which also
// surfaces an account that was locked or disabled after sign-in.
const readStoredAccessToken = (): string | null => {
  const stored = sessionStorage.getItem("AccessToken");
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);
    if (parsed?.expirationDate && new Date(parsed.expirationDate).getTime() <= Date.now()) return null;
    return parsed?.value || null;
  } catch {
    return stored;
  }
};

interface UserInfo {
  // Define your user info type here
  // Example: name: string;
}

interface Props {
  children: React.ReactNode;
}

export const PageLayout: React.FC<Props> = (props) => {
  const { instance, accounts } = useMsal();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  //const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [accessTokenFetched, setAccessTokenFetched] = useState<boolean>(false);
  // A token redirect that failed (saved in index.tsx) is shown instead of trying again in a loop.
  const [accessError, setAccessError] = useState<AccessError | null>(() => readLoginError());

  const request = { ...loginRequest, account: accounts[0] };
  const profileRequestOptions = { ...profileRequest, account: accounts[0] };

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        // Another account signed in on this tab: drop the previous user's cached data first.
        const username = accounts?.[0]?.username;
        const storedEmail = sessionStorage.getItem('email');
        if (username && storedEmail && storedEmail.toLowerCase() !== username.toLowerCase()) {
          clearUserScopedStorage();
        }

        // ดึง accessToken จาก sessionStorage ก่อน ถ้ายังไม่หมดอายุไม่ต้อง acquire ใหม่
        let accessToken = readStoredAccessToken();
        if (!accessToken) {
          const response = await instance.acquireTokenSilent(request);
          accessToken = response.accessToken;
          sessionSet("AccessToken", accessToken, 1);
        }
        sessionStorage.removeItem(interactionRedirectKey);
        if (username) {
          sessionStorage.setItem('email', username);
          // Start the Graph employeeId lookup now so API calls find it ready.
          getPersonId(username);
        }
        if (!cancelled) {
          setIsLoading(false);
          fetchMsGraphGroup();
        }
      } catch (error) {
        if (!cancelled) handleError(error, request);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [accounts]); // เพิ่ม accounts ใน dependency เผื่อ login เปลี่ยน

  const fetchMsGraphGroup = async () => {
    try {
      if (
        !sessionStorage.getItem("usergroup") ||
        sessionStorage.getItem("usergroup") === "undefined"
      ) {
        const response = await instance.acquireTokenSilent(
          profileRequestOptions
        );
        sessionSet("GraphAccessToken", response.accessToken, 60);
        
        // ดึงรูปโปรไฟล์และ group พร้อมกัน และรอให้เสร็จ
        await Promise.all([
          fetchUserPhoto(response.accessToken),
          callMsGraphGroup(response.accessToken).then(group => {
            sessionStorage.setItem("usergroup", JSON.stringify(group));
          })
        ]);
      } else {
        // ถ้ามี usergroup แล้ว แต่ยังไม่มี userPhoto ให้ดึงรูป
        const storedToken = sessionStorage.getItem("GraphAccessToken");
        if (storedToken && (!localStorage.getItem("userPhoto") || localStorage.getItem("userPhoto") === "")) {
          try {
            const tokenData = JSON.parse(storedToken);
            if (tokenData.value) {
              await fetchUserPhoto(tokenData.value);
            }
          } catch (e) {
            console.error("Error parsing stored token:", e);
          }
        }
      }
    } catch (error) {
      handleError(error, profileRequestOptions);
    }
  };

    // เพิ่มฟังก์ชันดึงรูปโปรไฟล์จาก Microsoft Graph
  const fetchUserPhoto = async (accessToken: string) => {
    try {
      const existingPhoto = localStorage.getItem("userPhoto");
      
      // ตรวจสอบว่ามีรูปอยู่แล้วและไม่ใช่ค่า empty หรือ "no-photo"
      if (existingPhoto && existingPhoto !== "" && existingPhoto !== "no-photo") {
        console.log("User photo already exists in localStorage");
        return;
      }
      
      console.log("Fetching user photo from Microsoft Graph...");
      
      // ตรวจสอบว่ามี photo หรือไม่ก่อน
      const photoMetadataEndpoint = "https://graph.microsoft.com/v1.0/me/photo";
      const metadataResponse = await axios.get(photoMetadataEndpoint, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      
      if (metadataResponse.data && metadataResponse.data["@odata.mediaContentType"]) {
        console.log("User has profile photo, downloading...");
        
        const graphEndpoint = "https://graph.microsoft.com/v1.0/me/photo/$value";
        const response = await axios.get(graphEndpoint, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          responseType: "arraybuffer",
        });
        
        // ตรวจสอบ response.data ว่ามีข้อมูลหรือไม่
        if (response.data && response.data.byteLength > 0) {
          console.log("Photo downloaded successfully, converting to base64...");
          // ใช้ btoa สำหรับ browser (Buffer ใช้ได้เฉพาะ Node.js)
          const binary = new Uint8Array(response.data)
            .reduce((data, byte) => data + String.fromCharCode(byte), "");
          const base64data = `data:image/jpeg;base64,${btoa(binary)}`;
          
          // ใช้ try-catch สำหรับการ set localStorage
          try {
            localStorage.setItem("userPhoto", base64data);
            console.log("User photo saved to localStorage successfully");
          } catch (storageError) {
            console.error("Error saving photo to localStorage:", storageError);
          }
        } else {
          console.log("No photo data received");
          localStorage.setItem("userPhoto", "no-photo");
        }
      } else {
        console.log("User does not have a profile photo");
        // เก็บค่า default หรือ placeholder
        localStorage.setItem("userPhoto", "no-photo");
      }
    } catch (error: any) {
      console.error("Error fetching user photo:", error);
      if (error.response) {
        console.error("Response status:", error.response.status);
        console.error("Response data:", error.response.data);
      }
      // เก็บค่า default เมื่อเกิด error
      try {
        localStorage.setItem("userPhoto", "no-photo");
      } catch (storageError) {
        console.error("Error setting fallback photo:", storageError);
      }
    }
  };

  // failedRequest is the token request that failed (API or Graph scopes), so the redirect asks for the right consent.
  const handleError = (error: any, failedRequest: any) => {
    console.log("PageLayout Error:", error);
    console.log("Error message:", error.message);

    // Token refresh rejected by Entra ID, e.g. the account was locked or disabled after sign-in.
    if (error instanceof InteractionRequiredAuthError) {
      const msalError = accessErrorFromMsal(error);
      if (msalError.code !== "SIGN_IN_FAILED") {
        setAccessError(msalError);
        return;
      }
      const lastRedirect = Number(sessionStorage.getItem(interactionRedirectKey) || 0);
      if (Date.now() - lastRedirect < interactionRedirectWindowMs) {
        // Already redirected for interaction a moment ago and it still fails: stop instead of looping.
        setAccessError(msalError);
        return;
      }
      sessionStorage.setItem(interactionRedirectKey, `${Date.now()}`);
      instance.acquireTokenRedirect(failedRequest).catch(() => undefined);
      return;
    }

    // ป้องกันการ reload ที่อาจทำให้ localStorage หาย
    if (
      error.message === "AxiosError: Network Error" ||
      error.message === "AxiosError: Request failed with status code 500"
    ) {
      setIsLoading(false);
      // เพิ่ม delay เล็กน้อยก่อน reload เพื่อให้ localStorage มีเวลา save
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }

    if (error.message === "AxiosError: Request failed with status code 400") {
      setIsLoading(false);
     // setUserInfo(error.message);
    }
  };

  const sessionSet = (key: string, value: any, expirationInMin: number = 10) => {
    const expirationDate = new Date(
      new Date().getTime() + 60000 * expirationInMin
    );
    const newValue = {
      value: value,
      expirationDate: expirationDate.toISOString(),
    };
    sessionStorage.setItem(key, JSON.stringify(newValue));
  };

  if (accessError) return <AccessErrorPage error={accessError} />;

  return (
    <>
      {isLoading ? (
        <div className="layout-loading">
          <div className="lds-ring">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      ) : (
        props.children
      )}
    </>
  );
};
